import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Wallet as WalletIcon } from 'lucide-react';
import { useDataService } from '@/lib/service-context';
import { Pagination } from '@/components/common/Pagination';
import { TabsBar } from '@/components/common/TabsBar';
import { formatTND } from '@/lib/format';
import type { WalletMovementStatus } from '@erp/domain/pro';

type Tab = 'all' | WalletMovementStatus;

export function PortefeuillePage() {
  const { t } = useTranslation();
  const ds = useDataService();
  const [tab, setTab] = useState<Tab>('all');
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const balanceQuery = useQuery({
    queryKey: ['pro-wallet-balance'],
    queryFn: () => ds.proWallet.getBalance(),
  });
  const movementsQuery = useQuery({
    queryKey: ['pro-wallet-movements', tab],
    queryFn: () => ds.proWallet.listMovements(tab === 'all' ? undefined : { status: tab }),
  });

  const list = movementsQuery.data ?? [];
  const paged = list.slice((page - 1) * pageSize, page * pageSize);

  const fmtDate = (s: string) => {
    try {
      const d = new Date(s);
      return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return s;
    }
  };

  return (
    <div className="space-y-4">
      {/* Balance hero card */}
      <div className="pro-card p-6 flex items-center gap-5">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: 'var(--pro-primary-subtle)' }}
        >
          <WalletIcon className="w-7 h-7" style={{ color: 'var(--pro-primary)' }} />
        </div>
        <div className="flex-1">
          <div
            className="text-[14px]"
            style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
          >
            {t('pro.portefeuille.balance', 'Commission disponible')}
          </div>
          <div
            className="text-[28px] font-bold mt-0.5"
            style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
          >
            {formatTND(balanceQuery.data?.balance ?? 0)}
          </div>
          {(balanceQuery.data?.pendingCommission ?? 0) > 0 && (
            <div className="text-[12px] mt-1" style={{ color: 'var(--pro-text-grey)' }}>
              {t('pro.portefeuille.pending', 'En attente de validation')} :{' '}
              {formatTND(balanceQuery.data?.pendingCommission ?? 0)}
            </div>
          )}
        </div>
      </div>

      {/* Movements card */}
      <div className="pro-card overflow-hidden">
        <div className="border-b px-4" style={{ borderColor: 'var(--pro-line)' }}>
          <TabsBar
            variant="underline"
            tabs={(['all', 'pending', 'approved', 'rejected'] as Tab[]).map((tk) => ({
              key: tk,
              label: t(`pro.portefeuille.tab_${tk}`, tk),
            }))}
            value={tab}
            onChange={(tk) => {
              setTab(tk);
              setPage(1);
            }}
          />
        </div>

        <div key={tab} className="pro-fade-in">
        {movementsQuery.isLoading ? (
          <div className="p-12 text-center" style={{ color: 'var(--pro-text-grey)' }}>
            …
          </div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center" style={{ color: 'var(--pro-text-grey)' }}>
            {t('pro.portefeuille.empty', 'Aucun mouvement')}
          </div>
        ) : (
          <>
          {/* Mobile: stacked movement rows */}
          <div className="md:hidden">
            {paged.map((m) => (
              <div
                key={m.id}
                className="px-4 py-3 flex items-center justify-between gap-3"
                style={{ borderBottom: '1px solid var(--pro-line)' }}
              >
                <div className="min-w-0">
                  <div
                    className="text-[13px] font-medium truncate"
                    style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                  >
                    {m.referenceCode ?? '—'}
                  </div>
                  <div className="text-[12px] truncate" style={{ color: 'var(--pro-text-grey)' }}>
                    {fmtDate(m.date)} · {t(`pro.portefeuille.type.${m.type}`, m.type)}
                  </div>
                </div>
                <div
                  className="text-[14px] font-semibold shrink-0"
                  style={{ color: m.amount >= 0 ? '#1DAF61' : 'var(--pro-danger)' }}
                >
                  {m.amount >= 0 ? '+' : ''}
                  {formatTND(m.amount)}
                </div>
              </div>
            ))}
          </div>
          {/* Desktop: full table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--pro-line)' }}>
                  <Th>ID de commande</Th>
                  <Th>Date</Th>
                  <Th>Description</Th>
                  <Th className="text-right">Commission</Th>
                </tr>
              </thead>
              <tbody>
                {paged.map((m) => (
                  <tr
                    key={m.id}
                    style={{ borderBottom: '1px solid var(--pro-line)' }}
                    className="hover:bg-orange-50/40"
                  >
                    <td
                      className="py-3 px-3 font-medium"
                      style={{ color: 'var(--pro-text-dark)' }}
                    >
                      {m.referenceCode ?? '—'}
                    </td>
                    <td className="py-3 px-3" style={{ color: 'var(--pro-text-dark)' }}>
                      {fmtDate(m.date)}
                    </td>
                    <td className="py-3 px-3" style={{ color: 'var(--pro-text-grey)' }}>
                      {t(`pro.portefeuille.type.${m.type}`, m.type)}
                    </td>
                    <td
                      className="py-3 px-3 text-right font-semibold"
                      style={{ color: m.amount >= 0 ? '#1DAF61' : 'var(--pro-danger)' }}
                    >
                      {m.amount >= 0 ? '+' : ''}
                      {formatTND(m.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {list.length > pageSize && (
          <div className="px-4 py-3" style={{ borderTop: '1px solid var(--pro-line)' }}>
            <Pagination page={page} pageSize={pageSize} total={list.length} onPageChange={setPage} />
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={`text-left py-3 px-3 text-[12px] font-medium ${className ?? ''}`}
      style={{ color: 'var(--pro-text-grey)', fontFamily: 'Figtree' }}
    >
      {children}
    </th>
  );
}
